// tecnicosService.js — fuente única del roster de técnicos (colección `tecnicos`)
//
// Migración desde utils/constants.js (TECNICOS_EMPLEADOS + tecnicoColors +
// calendario). Mientras la colección esté vacía o no disponible, todo cae al
// fallback derivado de las constantes, así que ningún consumidor se rompe
// antes de sembrar los datos.
import { db } from '../config/firebase';
import {
  collection, doc, getDocs, onSnapshot, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { TECNICOS_FALLBACK } from '../utils/constants';

const COL = 'tecnicos';
const DIAS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes'];

// Normaliza un doc de Firestore o un item del fallback a la forma canónica.
export function normalizeTecnico(raw = {}, idx = 0) {
  const zonas = {};
  DIAS.forEach(d => {
    const v = raw.zonas && raw.zonas[d];
    zonas[d] = Array.isArray(v) ? v : (v ? [String(v)] : []);
  });
  return {
    id:         String(raw.id ?? raw.employeeId ?? '').trim(),
    employeeId: Number(raw.employeeId ?? raw.id) || null,
    nombre:     (raw.nombre || raw.name || '').trim(),
    color:      raw.color || '#6B7280',
    zonas,
    activo:     raw.activo !== false,
    orden:      Number.isFinite(raw.orden) ? raw.orden : idx,
  };
}

const sortRoster = list => [...list].sort((a, b) => (a.orden - b.orden) || a.nombre.localeCompare(b.nombre, 'es'));

export const tecnicosFallback = () => sortRoster(TECNICOS_FALLBACK.map((t, i) => normalizeTecnico(t, i)));

// ── Cache de módulo ─────────────────────────────────────────────────────────
// Para consumidores NO reactivos (funciones puras, cálculos fuera de React).
// Se hidrata una vez al arrancar la app (hydrateTecnicos) y la mantiene viva
// cualquier suscripción activa de useTecnicos.
let _cache = tecnicosFallback();

export const getTecnicosCache = () => _cache;

export const getTecnicoNombreById = (id) => {
  const key = String(id);
  return _cache.find(t => t.id === key || String(t.employeeId) === key)?.nombre || null;
};

export const getTecnicoColorByNombre = (nombre) =>
  _cache.find(t => t.nombre === nombre)?.color || null;

function fromSnapshot(snap, idx0 = 0) {
  if (snap.empty) return tecnicosFallback();
  return sortRoster(snap.docs.map((d, i) => normalizeTecnico({ id: d.id, ...d.data() }, idx0 + i)));
}

export async function hydrateTecnicos() {
  try {
    const snap = await getDocs(collection(db, COL));
    _cache = fromSnapshot(snap);
  } catch (e) {
    console.warn('[tecnicosService] hydrate falló, uso fallback:', e.message);
    _cache = tecnicosFallback();
  }
  return _cache;
}

export function subscribeTecnicos(cb) {
  return onSnapshot(
    collection(db, COL),
    (snap) => { _cache = fromSnapshot(snap); cb(_cache, { fromFallback: snap.empty }); },
    (err) => { console.warn('[tecnicosService] subscribe error:', err.message); cb(tecnicosFallback(), { fromFallback: true, error: err.message }); },
  );
}

export async function saveTecnico(t) {
  const id = String(t.employeeId ?? t.id ?? '').trim();
  if (!id) throw new Error('El ID de empleado (StelOrder) es obligatorio');
  if (!String(t.nombre || '').trim()) throw new Error('El nombre es obligatorio');
  const norm = normalizeTecnico({ ...t, id });
  await setDoc(doc(db, COL, id), {
    employeeId:    norm.employeeId,
    nombre:        norm.nombre,
    color:         norm.color,
    zonas:         norm.zonas,
    activo:        norm.activo,
    orden:         norm.orden,
    actualizadoEn: serverTimestamp(),
  }, { merge: true });
  return id;
}

// Siembra / restaura la colección con el roster por defecto (merge, no borra).
export async function seedTecnicosDefault() {
  const base = tecnicosFallback();
  await Promise.all(base.map(t => setDoc(doc(db, COL, t.id), {
    employeeId:    t.employeeId,
    nombre:        t.nombre,
    color:         t.color,
    zonas:         t.zonas,
    activo:        true,
    orden:         t.orden,
    creadoEn:      serverTimestamp(),
    actualizadoEn: serverTimestamp(),
  }, { merge: true })));
  return base.length;
}

export const DIAS_SEMANA = DIAS;
